import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Bell, BellOff, AlertTriangle, Check, Settings as SettingsIcon, Info, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Notifications({ user }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [n, p] = await Promise.all([
          axios.get(`${API}/notifications`, { withCredentials: true }),
          axios.get(`${API}/notifications/preferences`, { withCredentials: true })
        ]);
        setNotifications(n.data);
        setPrefs(p.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const requestPermission = async () => {
    if (typeof Notification === 'undefined') {
      alert('Notifications not supported in this browser');
      return;
    }
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted') {
      // Show test notification
      new Notification('Homegrown', {
        body: 'You\'ll now get smart alerts when your garden needs you 🌱',
        icon: '/favicon.ico'
      });
      // Mark as subscribed
      try {
        await axios.post(`${API}/notifications/subscribe`, { subscription: { type: 'browser', granted_at: new Date().toISOString() } }, { withCredentials: true });
      } catch (e) { /* already granted elsewhere — ignore */ }
    }
  };

  const generateNow = async () => {
    try {
      await axios.post(`${API}/notifications/generate`, {}, { withCredentials: true });
      const n = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(n.data);
      // Show browser notification for highest priority
      if (permission === 'granted' && n.data.length > 0) {
        const critical = n.data.find(x => x.priority === 'critical');
        const top = critical || n.data[0];
        new Notification(`Homegrown · ${top.title}`, { body: top.body, icon: '/favicon.ico' });
      }
    } catch (e) { console.error(e); }
  };

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications(notifications.map(n => n.notification_id === id ? { ...n, read: true } : n));
    } catch (e) { console.error(e); }
  };

  const updatePref = async (key, value) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    try {
      await axios.patch(`${API}/notifications/preferences`, { [key]: value }, { withCredentials: true });
    } catch (e) { console.error(e); }
  };

  const openNotification = (n) => {
    if (!n.read) markRead(n.notification_id);
    if (n.deep_link) navigate(n.deep_link);
  };

  const priorityColor = (p) => {
    if (p === 'critical') return 'border-l-[#E2725B]';
    if (p === 'important') return 'border-l-[#D4AF37]';
    return 'border-l-[#8AB4F8]';
  };

  if (loading) return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div></div></Layout>;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</h1>
            <p className="text-base text-[#5C665C] mt-2">Smart alerts when timing matters</p>
          </div>
          <button onClick={() => setShowSettings(!showSettings)} data-testid="toggle-settings-button" className="border-2 border-[#8EAA97] text-[#1A4331] font-semibold py-3 px-6 rounded-full hover:bg-[#8EAA97]/10 transition-colors flex items-center gap-2">
            <SettingsIcon className="w-4 h-4" />Settings
          </button>
        </div>

        {/* Permission CTA */}
        {permission === 'default' && (
          <div className="bg-gradient-to-br from-[#1A4331] to-[#255C44] rounded-3xl p-6 text-white" data-testid="permission-cta">
            <div className="flex items-start gap-4">
              <Bell className="w-8 h-8 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Enable smart reminders</h2>
                <p className="text-white/90 text-sm mt-2 leading-relaxed">
                  Get notified about frost risk, watering windows, harvest timing, and pest pressure — only when it actually matters. We won&apos;t spam you.
                </p>
                <button onClick={requestPermission} data-testid="enable-notifications-button" className="mt-4 bg-white text-[#1A4331] font-semibold py-2 px-6 rounded-full hover:bg-[#FAF9F6] transition-colors">
                  Enable Notifications
                </button>
              </div>
            </div>
          </div>
        )}

        {permission === 'granted' && (
          <div className="bg-[#B4D330]/10 border border-[#B4D330]/30 rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-[#1A4331]" />
              <p className="text-sm text-[#1A4331] font-medium">Browser notifications enabled</p>
            </div>
            <button onClick={generateNow} data-testid="refresh-notifications-button" className="text-sm font-semibold text-[#1A4331] hover:text-[#255C44]">
              Refresh now
            </button>
          </div>
        )}

        {/* Settings Panel */}
        {showSettings && prefs && (
          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="settings-panel">
            <h2 className="text-xl font-semibold text-[#1A4331] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Notification Preferences</h2>
            <div className="space-y-2">
              {[
                { key: 'daily_briefing_enabled', label: 'Daily morning briefing', desc: 'One useful summary each morning' },
                { key: 'weekly_plan_enabled', label: 'Weekly garden plan', desc: 'Sunday morning planning ritual' },
                { key: 'weather_alerts_enabled', label: 'Weather risk alerts', desc: 'Frost, heat, wind, storms' },
                { key: 'watering_alerts_enabled', label: 'Watering reminders', desc: 'Smart, only when needed' },
                { key: 'pest_alerts_enabled', label: 'Pest pressure alerts', desc: 'Based on season and crops' },
                { key: 'harvest_alerts_enabled', label: 'Harvest timing', desc: 'Don\'t miss the window' },
                { key: 'feeding_alerts_enabled', label: 'Feeding reminders', desc: 'For heavy-feeding crops' },
                { key: 'learning_tips_enabled', label: 'Learning tips', desc: 'One practical tip a day' },
                { key: 'evening_wrap_enabled', label: 'Evening wrap-up', desc: '30-second daily check-in' }
              ].map(item => (
                <label key={item.key} className="flex items-center justify-between p-3 hover:bg-[#F0F2EB] rounded-xl cursor-pointer">
                  <div>
                    <p className="font-medium text-[#2D241E]">{item.label}</p>
                    <p className="text-xs text-[#5C665C]">{item.desc}</p>
                  </div>
                  <input type="checkbox" checked={prefs[item.key] || false} onChange={e => updatePref(item.key, e.target.checked)} data-testid={`pref-${item.key}`} className="w-5 h-5 rounded accent-[#1A4331]" />
                </label>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-[#1A4331]/5">
              <label className="block text-sm font-semibold text-[#2D241E] mb-2">Frequency</label>
              <div className="grid grid-cols-3 gap-2">
                {['minimal', 'balanced', 'active'].map(mode => (
                  <button key={mode} onClick={() => updatePref('frequency_mode', mode)} data-testid={`freq-${mode}`} className={`py-2 rounded-xl text-sm font-medium capitalize transition-all ${
                    prefs.frequency_mode === mode ? 'bg-[#1A4331] text-white' : 'bg-[#F0F2EB] text-[#2D241E]'
                  }`}>
                    {mode}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Notification Feed */}
        {notifications.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 border border-[#1A4331]/5 shadow-sm text-center" data-testid="empty-state">
            <BellOff className="w-16 h-16 text-[#8EAA97] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#2D241E] mb-2">All caught up</h3>
            <p className="text-[#5C665C] mb-6">Your garden has no urgent alerts right now.</p>
            <button onClick={generateNow} data-testid="generate-now-button" className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-8 rounded-full transition-colors">
              Check for alerts now
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map(n => (
              <div key={n.notification_id} data-testid={`notif-${n.notification_id}`} className={`bg-white rounded-2xl border border-[#1A4331]/5 border-l-4 ${priorityColor(n.priority)} shadow-sm overflow-hidden ${n.read ? 'opacity-60' : ''} ${n.deep_link ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                onClick={n.deep_link ? () => openNotification(n) : undefined}>
                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className={`w-5 h-5 mt-1 flex-shrink-0 ${
                      n.priority === 'critical' ? 'text-[#E2725B]' :
                      n.priority === 'important' ? 'text-[#D4AF37]' :
                      'text-[#8AB4F8]'
                    }`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold tracking-wider uppercase ${
                          n.priority === 'critical' ? 'text-[#E2725B]' :
                          n.priority === 'important' ? 'text-[#D4AF37]' :
                          'text-[#8AB4F8]'
                        }`}>{n.priority}</span>
                        <span className="text-xs text-[#5C665C]">· Confidence: {n.confidence}</span>
                      </div>
                      <h3 className="font-semibold text-[#2D241E]">{n.title}</h3>
                      <p className="text-sm text-[#5C665C] mt-1">{n.body}</p>
                      {n.based_on && n.based_on.length > 0 && (
                        <div className="mt-3 flex items-start gap-2 text-xs text-[#5C665C]">
                          <Info className="w-3 h-3 mt-0.5" />
                          <div className="flex flex-wrap gap-1">
                            {n.based_on.map((b, i) => (
                              <span key={i} className="px-2 py-0.5 bg-[#F0F2EB] rounded-full">{b.replace(/_/g, ' ')}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {n.deep_link && <ChevronRight className="w-5 h-5 text-[#8EAA97] mt-1 flex-shrink-0" data-testid={`notif-open-${n.notification_id}`} />}
                    {!n.read && (
                      <button onClick={(e) => { e.stopPropagation(); markRead(n.notification_id); }} data-testid={`mark-read-${n.notification_id}`} className="text-xs text-[#5C665C] hover:text-[#1A4331]">
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Notifications;
